import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import {
  isUsableProfilePhotoUrl,
  resolveAuthorColor,
} from '../platform/chatAuthorColors';
import { resolveProfileDisplayName } from '../platform/displayName';

function emptyAuthorsMap() {
  return {};
}

function mapAuthorMeta(uid, data = {}) {
  return {
    displayName: resolveProfileDisplayName(data),
    photoURL: isUsableProfilePhotoUrl(data.photoURL) ? data.photoURL : '',
    chatColor: resolveAuthorColor(uid, data.chatColor),
  };
}

export function useMessageAuthors(messages) {
  const [authorsByUid, setAuthorsByUid] = useState(emptyAuthorsMap);

  const uidKey = useMemo(
    () => [...new Set((messages || []).map((message) => message.uid).filter(Boolean))].sort().join('|'),
    [messages],
  );

  useEffect(() => {
    const uids = uidKey ? uidKey.split('|') : [];
    if (uids.length === 0) {
      setAuthorsByUid(emptyAuthorsMap());
      return undefined;
    }

    const unsubs = uids.map((uid) =>
      onSnapshot(
        doc(db, 'users', uid),
        (snapshot) => {
          const data = snapshot.exists() ? snapshot.data() : {};
          setAuthorsByUid((prev) => ({
            ...prev,
            [uid]: mapAuthorMeta(uid, data),
          }));
        },
        () => {
          setAuthorsByUid((prev) => ({
            ...prev,
            [uid]: mapAuthorMeta(uid),
          }));
        },
      ),
    );

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [uidKey]);

  return authorsByUid;
}
